import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Box, Grid, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const QuestionReportDialog = ({
  open,
  onClose,
  reportData,
  selectedSurvey,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Soru Raporu
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        {reportData && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 600, color: '#444444', mb: 0.5 }}>
                {reportData.questionText}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                Toplam Cevaplar: {reportData.totalAnswers || 0}
              </Typography>
            </Box>
            {reportData.questionType === 0 && reportData.optionCounts && (
              <Box>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 2 }}>
                  Seçenek Dağılımı
                </Typography>
                <Stack spacing={1.5}>
                  {Object.entries(reportData.optionCounts).map(([index, count]) => {
                    const optionIndex = Number(index);
                    const currentQuestion = selectedSurvey?.questions?.find(q => q.id === reportData.questionId);
                    const optionText = currentQuestion?.options?.[optionIndex] || `Seçenek ${optionIndex + 1}`;
                    const percentage = reportData.totalAnswers > 0
                      ? ((count / reportData.totalAnswers) * 100).toFixed(1)
                      : 0;
                    return (
                      <Box key={index}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                          <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>
                            {optionText}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#0E4D94' }}>
                            {count} ({percentage}%)
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            height: 28,
                            bgcolor: '#f5f5f5',
                            borderRadius: '6px',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid #e6e6e6',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${percentage}%`,
                              bgcolor: '#0E4D94',
                              transition: 'width 0.3s ease',
                              borderRadius: '6px',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
            {reportData.questionType === 1 && reportData.ratingStats && (
              <Box>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 2 }}>
                  Değerlendirme İstatistikleri
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Ortalama
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 700, color: '#0E4D94' }}>
                      {reportData.ratingStats.averageRating?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Min / Max
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                      {reportData.ratingStats.minRating || 1} / {reportData.ratingStats.maxRating || 5}
                    </Typography>
                  </Grid>
                </Grid>
                {reportData.ratingStats.ratingCounts && (
                  <Stack spacing={1.5}>
                    {Object.entries(reportData.ratingStats.ratingCounts)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([rating, count]) => {
                        const percentage = reportData.totalAnswers > 0
                          ? ((count / reportData.totalAnswers) * 100).toFixed(1)
                          : 0;
                        return (
                          <Box key={rating}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                              <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>
                                {rating} Yıldız
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#FF9800' }}>
                                {count} ({percentage}%)
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                height: 28,
                                bgcolor: '#f5f5f5',
                                borderRadius: '6px',
                                position: 'relative',
                                overflow: 'hidden',
                                border: '1px solid #e6e6e6',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${percentage}%`,
                                  bgcolor: '#FF9800',
                                  transition: 'width 0.3s ease',
                                  borderRadius: '6px',
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                  </Stack>
                )}
              </Box>
            )}
            {reportData.questionType === 2 && reportData.numberStats && (
              <Box>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 2 }}>
                  Sayısal İstatistikler
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Ortalama
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 700, color: '#0E4D94' }}>
                      {reportData.numberStats.average?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Toplam
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 700, color: '#0E4D94' }}>
                      {reportData.numberStats.sum?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Minimum
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                      {reportData.numberStats.min?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Maksimum
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                      {reportData.numberStats.max?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mb: 0.5 }}>
                      Medyan
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                      {reportData.numberStats.median?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionReportDialog;